<template>
  <div class="pet-list-page">
    <aside class="pet-list-sidebar page-card">
      <div class="page-card__head">
        <div>
          <div class="page-card__title">大类分类</div>
          <div class="page-card__desc">按一级宠物类型快速筛选宠物资料。</div>
        </div>
      </div>

      <div class="pet-list-sidebar__scroll">
        <button
          class="pet-list-category"
          :class="{ 'pet-list-category--active': selectedLevel1Code === '' }"
          @click="selectLevel1('')"
        >
          <span>全部</span>
          <strong>{{ pets.length }}</strong>
        </button>

        <button
          v-for="item in level1Options"
          :key="item.typeCode"
          class="pet-list-category"
          :class="{ 'pet-list-category--active': selectedLevel1Code === item.typeCode }"
          @click="selectLevel1(item.typeCode)"
        >
          <span>{{ item.typeName }}</span>
          <strong>{{ countPetsByNode(item) }}</strong>
        </button>
      </div>
    </aside>

    <section class="pet-list-main page-card">
      <div class="page-card__head">
        <div>
          <div class="page-card__title">宠物资料</div>
          <div class="page-card__desc">统一查看用户创建的宠物档案，支持按类型层级快速筛选。</div>
        </div>
        <button class="action-button">新增资料</button>
      </div>

      <div class="pet-list-filters">
        <div class="pet-list-filter-group">
          <span class="pet-list-filter-group__label">二级分类</span>
          <div class="pet-list-chip-list">
            <button
              class="pet-list-chip"
              :class="{ 'pet-list-chip--active': selectedLevel2Code === '' }"
              :disabled="!selectedLevel1Node"
              @click="selectLevel2('')"
            >
              全部
            </button>
            <button
              v-for="item in level2Options"
              :key="item.typeCode"
              class="pet-list-chip"
              :class="{ 'pet-list-chip--active': selectedLevel2Code === item.typeCode }"
              @click="selectLevel2(item.typeCode)"
            >
              {{ item.typeName }}
            </button>
            <span v-if="!selectedLevel1Node" class="pet-list-filter-hint">先选左侧大类</span>
          </div>
        </div>

        <div class="pet-list-filter-group">
          <span class="pet-list-filter-group__label">三级分类</span>
          <div class="pet-list-chip-list">
            <button
              class="pet-list-chip"
              :class="{ 'pet-list-chip--active': selectedLevel3Code === '' }"
              :disabled="!selectedLevel2Node"
              @click="selectLevel3('')"
            >
              全部
            </button>
            <button
              v-for="item in level3Options"
              :key="item.typeCode"
              class="pet-list-chip"
              :class="{ 'pet-list-chip--active': selectedLevel3Code === item.typeCode }"
              @click="selectLevel3(item.typeCode)"
            >
              {{ item.typeName }}
            </button>
            <span v-if="!selectedLevel2Node" class="pet-list-filter-hint">先选二级分类</span>
          </div>
        </div>
      </div>

      <div v-if="loading" class="page-state pet-list-state">加载中...</div>

      <div v-else class="pet-list-table-wrap">
        <table class="table pet-list-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>名字</th>
              <th>品类</th>
              <th>阶段</th>
              <th>所属用户</th>
              <th>标签</th>
              <th>状态</th>
              <th class="sticky-action-column">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in pagedPets" :key="item.id">
              <td>{{ item.id }}</td>
              <td>{{ item.name || "-" }}</td>
              <td>{{ item.species || "-" }}</td>
              <td>{{ item.stage || "-" }}</td>
              <td>{{ item.ownerNickname || item.ownerUserId }}</td>
              <td>{{ item.careTags || "-" }}</td>
              <td>{{ item.status === 1 ? "正常" : "停用" }}</td>
              <td class="sticky-action-column">
                <div class="pet-list-actions">
                  <button class="table-action-button" type="button" @click="openPetDetail(item, 'profile')">资料</button>
                  <button
                    class="table-action-button table-action-button--ghost"
                    type="button"
                    :disabled="!canLoadPetData(item)"
                    @click="openPetDetail(item, 'care')"
                  >
                    照护
                  </button>
                  <button
                    class="table-action-button table-action-button--ghost"
                    type="button"
                    :disabled="!canLoadPetData(item)"
                    @click="openPetDetail(item, 'weight')"
                  >
                    体重
                  </button>
                  <button
                    class="table-action-button table-action-button--ghost"
                    type="button"
                    :disabled="!canLoadPetData(item)"
                    @click="openPetDetail(item, 'cost')"
                  >
                    成本
                  </button>
                  <button
                    class="table-action-button table-action-button--ghost"
                    type="button"
                    :disabled="!canLoadPetData(item)"
                    @click="openPetDetail(item, 'plan')"
                  >
                    计划
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="pagedPets.length === 0">
              <td colspan="8">{{ emptyText }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pet-list-footer">
        <div class="pet-list-footer__meta">
          共 {{ filteredPets.length }} 条
          <span v-if="filteredPets.length > 0">，当前 {{ pageStart }} - {{ pageEnd }}</span>
        </div>

        <div class="pet-list-pagination">
          <label class="pet-list-page-size">
            <span>每页</span>
            <select v-model.number="pageSize" @change="handlePageSizeChange">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
          </label>

          <button class="ghost-button" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">上一页</button>

          <button
            v-for="page in visiblePages"
            :key="page"
            class="pet-list-page-button"
            :class="{ 'pet-list-page-button--active': currentPage === page }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>

          <button class="ghost-button" :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">下一页</button>
        </div>
      </div>
    </section>

    <div v-if="petDetailVisible" class="pet-detail-modal" @click.self="closePetDetail">
      <div class="pet-detail-modal__panel" role="dialog" aria-modal="true">
        <div class="pet-detail-modal__head">
          <div>
            <div class="page-card__title">{{ petDetailTitle }}</div>
            <div class="page-card__desc">{{ activePet ? `${activePet.name || "未命名宠物"} · ${activePet.species || "未填写品类"}` : "宠物资料" }}</div>
          </div>
          <button class="modal-close-button" type="button" aria-label="关闭" @click="closePetDetail">×</button>
        </div>

        <div v-if="petDetailLoading" class="page-state pet-detail-state">数据加载中...</div>
        <div v-else-if="petDetailError" class="page-state pet-detail-state">{{ petDetailError }}</div>

        <div v-else class="pet-detail-body">
          <div v-if="petDetailType === 'profile'" class="pet-profile-detail">
            <div class="pet-cover-preview">
              <img v-if="activePet && activePet.coverUrl" :src="resolveFileUrl(activePet.coverUrl)" :alt="`${activePet.name || '宠物'}背景图`" />
              <span v-else>暂无背景图</span>
            </div>
            <div class="pet-profile-card">
              <div class="pet-avatar-preview">
                <img v-if="activePet && activePet.avatarUrl" :src="resolveFileUrl(activePet.avatarUrl)" :alt="`${activePet.name || '宠物'}头像`" />
                <span v-else>{{ petInitial(activePet) }}</span>
              </div>
              <div class="pet-profile-card__copy">
                <div class="pet-profile-card__name">{{ activePet.name || "未命名宠物" }}</div>
                <div class="pet-profile-card__meta">{{ activePet.species || "-" }} / {{ activePet.morph || "未填基因" }}</div>
                <div class="pet-profile-card__intro">{{ activePet.intro || "暂无简介" }}</div>
              </div>
            </div>
            <div class="pet-detail-grid">
              <div v-for="field in petProfileFields" :key="field.label" class="pet-detail-field">
                <span>{{ field.label }}</span>
                <strong>{{ field.value }}</strong>
              </div>
            </div>
          </div>

          <div v-else-if="petDetailType === 'care'" class="pet-detail-section">
            <table class="table pet-detail-table">
              <thead>
                <tr>
                  <th>类型</th>
                  <th>标题</th>
                  <th>内容</th>
                  <th>关键数据</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in petDetailData.careRecords" :key="record.id">
                  <td>{{ recordTypeLabel(record.recordType) }}</td>
                  <td>{{ record.title || "-" }}</td>
                  <td>{{ record.content || "-" }}</td>
                  <td>{{ careRecordSummary(record) }}</td>
                  <td>{{ formatDate(record.occurredAt) }}</td>
                </tr>
                <tr v-if="petDetailData.careRecords.length === 0">
                  <td colspan="5">暂无照护记录</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="petDetailType === 'weight'" class="pet-detail-section">
            <table class="table pet-detail-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>体重</th>
                  <th>来源</th>
                  <th>备注</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in petDetailData.weightRecords" :key="record.id">
                  <td>{{ record.id }}</td>
                  <td>{{ formatWeight(record.weightValue) }}</td>
                  <td>{{ record.sourceType || "-" }}</td>
                  <td>{{ record.note || "-" }}</td>
                  <td>{{ formatDate(record.measuredAt) }}</td>
                </tr>
                <tr v-if="petDetailData.weightRecords.length === 0">
                  <td colspan="5">暂无体重记录</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="petDetailType === 'cost'" class="pet-detail-section">
            <div class="pet-cost-summary">
              <div class="pet-detail-field">
                <span>累计花费</span>
                <strong>{{ formatMoney(petDetailData.costSummary.totalAmount) }}</strong>
              </div>
              <div class="pet-detail-field">
                <span>本月花费</span>
                <strong>{{ formatMoney(petDetailData.costSummary.latestMonthAmount) }}</strong>
              </div>
              <div class="pet-detail-field">
                <span>记录数</span>
                <strong>{{ petDetailData.costSummary.totalRecordCount || 0 }}</strong>
              </div>
            </div>
            <table class="table pet-detail-table">
              <thead>
                <tr>
                  <th>分类</th>
                  <th>项目</th>
                  <th>金额</th>
                  <th>备注</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in petDetailData.costRecords" :key="record.id">
                  <td>{{ record.categoryLabel || record.categoryCode || "-" }}</td>
                  <td>{{ record.itemName || "-" }}</td>
                  <td>{{ formatMoney(record.amount) }}</td>
                  <td>{{ record.note || "-" }}</td>
                  <td>{{ formatDate(record.occurredAt) }}</td>
                </tr>
                <tr v-if="petDetailData.costRecords.length === 0">
                  <td colspan="5">暂无成本记录</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="petDetailType === 'plan'" class="pet-detail-section">
            <div class="pet-detail-grid">
              <div class="pet-detail-field">
                <span>喂食记录</span>
                <strong>{{ petDetailData.feedingPlan.feedingRecordCount || 0 }}</strong>
              </div>
              <div class="pet-detail-field">
                <span>体重记录</span>
                <strong>{{ petDetailData.feedingPlan.weightRecordCount || 0 }}</strong>
              </div>
              <div class="pet-detail-field">
                <span>健康记录</span>
                <strong>{{ petDetailData.feedingPlan.healthRecordCount || 0 }}</strong>
              </div>
              <div class="pet-detail-field">
                <span>当前体重</span>
                <strong>{{ petDetailData.feedingPlan.currentWeightText || "-" }}</strong>
              </div>
            </div>
            <div class="pet-plan-block">
              <h4>手动计划</h4>
              <p>{{ feedingManualPlanText }}</p>
            </div>
            <div class="pet-plan-block">
              <h4>AI 计划</h4>
              <p>{{ petDetailData.feedingPlan.aiPlanSummary || petDetailData.feedingPlan.aiPlanDetails || "暂无 AI 计划" }}</p>
            </div>
          </div>
        </div>

        <div class="pet-detail-modal__foot">
          <span>{{ petDetailFooter }}</span>
          <button class="ghost-button" type="button" @click="closePetDetail">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchPetCareRecords,
  fetchPetCostRecords,
  fetchPetCostSummary,
  fetchPetFeedingPlan,
  fetchPetList,
  fetchPetOwner,
  fetchPetWeightRecords
} from "../api/pet"
import { fetchPetTypeTree } from "../api/petType"
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"

export default {
  name: "PetListView",
  data() {
    return {
      loading: false,
      pets: [],
      typeTree: [],
      typeNodeMap: {},
      subtreeNameMap: {},
      selectedLevel1Code: "",
      selectedLevel2Code: "",
      selectedLevel3Code: "",
      currentPage: 1,
      pageSize: 20,
      petDetailVisible: false,
      petDetailLoading: false,
      petDetailError: "",
      petDetailType: "profile",
      activePet: null,
      petDetailData: this.emptyPetDetailData()
    }
  },
  computed: {
    level1Options() {
      return this.typeTree
    },
    selectedLevel1Node() {
      return this.selectedLevel1Code ? this.typeNodeMap[this.selectedLevel1Code] || null : null
    },
    level2Options() {
      return this.selectedLevel1Node && Array.isArray(this.selectedLevel1Node.children)
        ? this.selectedLevel1Node.children
        : []
    },
    selectedLevel2Node() {
      return this.selectedLevel2Code ? this.typeNodeMap[this.selectedLevel2Code] || null : null
    },
    level3Options() {
      return this.selectedLevel2Node && Array.isArray(this.selectedLevel2Node.children)
        ? this.selectedLevel2Node.children
        : []
    },
    activeFilterNode() {
      if (this.selectedLevel3Code) {
        return this.typeNodeMap[this.selectedLevel3Code] || null
      }
      if (this.selectedLevel2Code) {
        return this.typeNodeMap[this.selectedLevel2Code] || null
      }
      if (this.selectedLevel1Code) {
        return this.typeNodeMap[this.selectedLevel1Code] || null
      }
      return null
    },
    filteredPets() {
      if (!this.activeFilterNode) {
        return this.pets
      }
      return this.pets.filter(item => this.matchesTypeNode(item.species, this.activeFilterNode))
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredPets.length / this.pageSize))
    },
    pagedPets() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredPets.slice(start, start + this.pageSize)
    },
    pageStart() {
      if (this.filteredPets.length === 0) {
        return 0
      }
      return (this.currentPage - 1) * this.pageSize + 1
    },
    pageEnd() {
      return Math.min(this.currentPage * this.pageSize, this.filteredPets.length)
    },
    visiblePages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, start + 4)
      const adjustedStart = Math.max(1, end - 4)
      for (let page = adjustedStart; page <= end; page += 1) {
        pages.push(page)
      }
      return pages
    },
    emptyText() {
      return this.activeFilterNode ? "当前筛选下暂无宠物资料" : "暂无宠物资料"
    },
    petDetailTitle() {
      const titleMap = {
        profile: "宠物详细资料",
        care: "照护数据",
        weight: "体重数据",
        cost: "成本数据",
        plan: "喂养计划"
      }
      return titleMap[this.petDetailType] || "宠物数据"
    },
    petDetailFooter() {
      if (!this.activePet) {
        return "宠物数据"
      }
      if (this.petDetailType === "care") {
        return `最近 ${this.petDetailData.careRecords.length} 条照护记录`
      }
      if (this.petDetailType === "weight") {
        return `最近 ${this.petDetailData.weightRecords.length} 条体重记录`
      }
      if (this.petDetailType === "cost") {
        return `最近 ${this.petDetailData.costRecords.length} 条成本记录`
      }
      return `宠物 ID ${this.activePet.id}`
    },
    petProfileFields() {
      if (!this.activePet) {
        return []
      }
      return [
        { label: "宠物 ID", value: this.formatText(this.activePet.id) },
        { label: "所属用户", value: this.formatText(this.activePet.ownerNickname || this.activePet.ownerUserId) },
        { label: "主人 OpenID", value: this.formatText(this.activePet.ownerOpenId) },
        { label: "品类编码", value: this.formatText(this.activePet.speciesCode) },
        { label: "品类分组", value: this.formatText(this.activePet.speciesGroupCode) },
        { label: "性别", value: this.formatText(this.activePet.sex) },
        { label: "阶段", value: this.formatText(this.activePet.stage) },
        { label: "当前体重", value: this.formatWeight(this.activePet.currentWeight) },
        { label: "到家体重", value: this.formatWeight(this.activePet.arrivalWeight) },
        { label: "孵化日期", value: this.formatDate(this.activePet.hatchDate) },
        { label: "到家日期", value: this.formatDate(this.activePet.adoptDate) },
        { label: "标签", value: this.formatText(this.activePet.careTags) },
        { label: "留种", value: this.activePet.holdbackFlag === 1 ? "是" : "否" },
        { label: "销售状态", value: this.formatText(this.activePet.saleStatus) },
        { label: "状态", value: this.activePet.status === 1 ? "正常" : "停用" },
        { label: "创建时间", value: this.formatDate(this.activePet.createdAt) },
        { label: "更新时间", value: this.formatDate(this.activePet.updatedAt) }
      ]
    },
    feedingManualPlanText() {
      const plan = this.petDetailData.feedingPlan || {}
      if (!plan.hasManualPlan) {
        return "暂无手动计划"
      }
      const parts = [
        plan.manualFrequency,
        plan.manualPortion,
        plan.manualFoods,
        plan.manualSupplements,
        plan.manualObservation,
        plan.manualNotes
      ].filter(Boolean)
      return parts.length > 0 ? parts.join(" / ") : "已配置手动计划"
    }
  },
  created() {
    this.loadPets()
  },
  methods: {
    async loadPets() {
      this.loading = true
      try {
        const [petRes, typeRes] = await Promise.all([
          fetchPetList(),
          fetchPetTypeTree().catch(() => null)
        ])
        this.pets = petRes.data || []
        this.applyTypeTree(typeRes && typeRes.data ? typeRes.data.items || [] : [])
        this.ensureCurrentPageInRange()
      } finally {
        this.loading = false
      }
    },
    applyTypeTree(nodes) {
      this.typeTree = Array.isArray(nodes) ? nodes : []
      const nodeMap = {}
      const subtreeNameMap = {}

      const walk = node => {
        if (!node || !node.typeCode) {
          return []
        }
        nodeMap[node.typeCode] = node
        let names = []
        const currentName = this.normalizeText(node.typeName)
        if (currentName) {
          names.push(currentName)
        }
        const children = Array.isArray(node.children) ? node.children : []
        children.forEach(child => {
          names = names.concat(walk(child))
        })
        subtreeNameMap[node.typeCode] = Array.from(new Set(names))
        return subtreeNameMap[node.typeCode]
      }

      this.typeTree.forEach(item => walk(item))
      this.typeNodeMap = nodeMap
      this.subtreeNameMap = subtreeNameMap
    },
    normalizeText(value) {
      return value == null ? "" : String(value).trim().toLowerCase()
    },
    matchesTypeNode(species, node) {
      const normalizedSpecies = this.normalizeText(species)
      if (!normalizedSpecies || !node || !node.typeCode) {
        return false
      }
      const candidates = this.subtreeNameMap[node.typeCode] || []
      return candidates.some(item => {
        if (!item) {
          return false
        }
        return normalizedSpecies === item || normalizedSpecies.includes(item) || item.includes(normalizedSpecies)
      })
    },
    countPetsByNode(node) {
      return this.pets.filter(item => this.matchesTypeNode(item.species, node)).length
    },
    canLoadPetData(pet) {
      return Boolean(pet && (pet.ownerOpenId || pet.ownerUserId))
    },
    emptyPetDetailData() {
      return {
        careRecords: [],
        weightRecords: [],
        costRecords: [],
        costSummary: {},
        feedingPlan: {}
      }
    },
    async openPetDetail(pet, type) {
      if (!pet || !pet.id) {
        return
      }
      this.activePet = pet
      this.petDetailType = type
      this.petDetailVisible = true
      this.petDetailLoading = false
      this.petDetailError = ""
      this.petDetailData = this.emptyPetDetailData()

      if (type === "profile") {
        return
      }
      if (!this.canLoadPetData(pet)) {
        this.petDetailError = "该宠物缺少主人 OpenID，无法查询记录类数据"
        return
      }

      this.petDetailLoading = true
      try {
        const openId = await this.resolvePetOpenId(pet)
        if (!openId) {
          this.petDetailError = "该宠物缺少主人 OpenID，无法查询记录类数据"
          return
        }
        if (type === "care") {
          const res = await fetchPetCareRecords(openId, pet.id)
          this.petDetailData.careRecords = res.data || []
        } else if (type === "weight") {
          const res = await fetchPetWeightRecords(openId, pet.id)
          this.petDetailData.weightRecords = res.data || []
        } else if (type === "cost") {
          const [summaryRes, recordsRes] = await Promise.all([
            fetchPetCostSummary(openId, pet.id),
            fetchPetCostRecords(openId, pet.id)
          ])
          this.petDetailData.costSummary = summaryRes.data || {}
          this.petDetailData.costRecords = recordsRes.data || []
        } else if (type === "plan") {
          const res = await fetchPetFeedingPlan(pet.ownerUserId, pet.id)
          this.petDetailData.feedingPlan = res.data || {}
        }
      } catch (error) {
        this.petDetailError = error && error.message ? error.message : "宠物数据加载失败"
      } finally {
        this.petDetailLoading = false
      }
    },
    async resolvePetOpenId(pet) {
      if (pet.ownerOpenId) {
        return pet.ownerOpenId
      }
      if (!pet.ownerUserId) {
        return ""
      }
      const res = await fetchPetOwner(pet.ownerUserId)
      const owner = res.data || {}
      const openId = owner.openId || ""
      if (openId) {
        this.$set(pet, "ownerOpenId", openId)
      }
      return openId
    },
    closePetDetail() {
      this.petDetailVisible = false
      this.petDetailLoading = false
      this.petDetailError = ""
      this.petDetailType = "profile"
      this.activePet = null
      this.petDetailData = this.emptyPetDetailData()
    },
    petInitial(pet) {
      const name = pet && pet.name ? pet.name : "宠"
      return name.slice(0, 1).toUpperCase()
    },
    resolveFileUrl(fileUrl) {
      return resolveBackendAssetUrl(fileUrl)
    },
    formatText(value, fallback = "-") {
      if (value === null || typeof value === "undefined" || value === "") {
        return fallback
      }
      return String(value)
    },
    formatDate(value) {
      if (!value) {
        return "-"
      }
      return String(value).replace("T", " ").slice(0, 19)
    },
    formatWeight(value) {
      if (value === null || typeof value === "undefined" || value === "") {
        return "-"
      }
      return `${value} g`
    },
    formatMoney(value) {
      const amount = Number(value || 0)
      return `¥${amount.toFixed(2)}`
    },
    recordTypeLabel(type) {
      const typeMap = {
        FEEDING: "投喂",
        HEALTH: "健康",
        ENVIRONMENT: "环境",
        WEIGHT: "体重",
        FECES: "排便"
      }
      return typeMap[type] || type || "-"
    },
    careRecordSummary(record) {
      const parts = []
      if (record.foodName) {
        parts.push(record.foodName)
      }
      if (record.feedingCount || record.feedingUnit) {
        parts.push(`${record.feedingCount || ""}${record.feedingUnit || ""}`)
      }
      if (record.weightValue) {
        parts.push(this.formatWeight(record.weightValue))
      }
      if (record.temperature) {
        parts.push(`${record.temperature}°C`)
      }
      if (record.humidity) {
        parts.push(`${record.humidity}%`)
      }
      if (record.moodStatus) {
        parts.push(record.moodStatus)
      }
      if (record.isRefused === 1) {
        parts.push("拒食")
      }
      return parts.length > 0 ? parts.join(" / ") : "-"
    },
    selectLevel1(typeCode) {
      this.selectedLevel1Code = typeCode
      this.selectedLevel2Code = ""
      this.selectedLevel3Code = ""
      this.currentPage = 1
    },
    selectLevel2(typeCode) {
      if (!this.selectedLevel1Node) {
        return
      }
      this.selectedLevel2Code = typeCode
      this.selectedLevel3Code = ""
      this.currentPage = 1
    },
    selectLevel3(typeCode) {
      if (!this.selectedLevel2Node) {
        return
      }
      this.selectedLevel3Code = typeCode
      this.currentPage = 1
    },
    handlePageSizeChange() {
      this.currentPage = 1
      this.ensureCurrentPageInRange()
    },
    goToPage(page) {
      const nextPage = Math.min(Math.max(page, 1), this.totalPages)
      this.currentPage = nextPage
    },
    ensureCurrentPageInRange() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages
      }
      if (this.currentPage < 1) {
        this.currentPage = 1
      }
    }
  }
}
</script>

<style scoped>
.pet-list-page {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 20px;
  min-height: calc(100vh - 150px);
}

.pet-list-sidebar,
.pet-list-main {
  min-height: 0;
  height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pet-list-sidebar__scroll {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 4px;
}

.pet-list-category {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #dbe3ee;
  border-radius: 14px;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
  text-align: left;
}

.pet-list-category span {
  font-size: 14px;
  font-weight: 600;
}

.pet-list-category strong {
  color: #64748b;
  font-size: 13px;
}

.pet-list-category--active {
  border-color: #2563eb;
  background: #eef4ff;
  color: #1d4ed8;
}

.pet-list-category--active strong {
  color: #1d4ed8;
}

.pet-list-main {
  min-width: 0;
}

.pet-list-filters {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
}

.pet-list-filter-group {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.pet-list-filter-group__label {
  width: 72px;
  flex: none;
  padding-top: 6px;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
}

.pet-list-chip-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.pet-list-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 14px;
  border: 1px solid #d7deea;
  border-radius: 999px;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  white-space: nowrap;
}

.pet-list-chip:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pet-list-chip--active {
  border-color: #2563eb;
  background: #eef4ff;
  color: #1d4ed8;
  font-weight: 700;
}

.pet-list-filter-hint {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  color: #94a3b8;
  font-size: 13px;
}

.pet-list-state {
  margin-bottom: 16px;
}

.pet-list-table-wrap {
  min-height: 0;
  flex: 1;
  overflow: auto;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  background: #ffffff;
}

.pet-list-table {
  min-width: 1056px;
}

.pet-list-table th:nth-child(1),
.pet-list-table td:nth-child(1) {
  width: 72px;
}

.pet-list-table th:nth-child(2),
.pet-list-table td:nth-child(2) {
  width: 140px;
}

.pet-list-table th:nth-child(3),
.pet-list-table td:nth-child(3) {
  width: 160px;
}

.pet-list-table th:nth-child(4),
.pet-list-table td:nth-child(4) {
  width: 100px;
}

.pet-list-table th:nth-child(5),
.pet-list-table td:nth-child(5) {
  width: 140px;
}

.pet-list-table th:nth-child(6),
.pet-list-table td:nth-child(6) {
  width: 160px;
}

.pet-list-table th:nth-child(7),
.pet-list-table td:nth-child(7) {
  width: 88px;
}

.pet-list-table th:nth-child(8),
.pet-list-table td:nth-child(8) {
  width: 196px;
}

.pet-list-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
}

.pet-list-table .sticky-action-column {
  position: sticky;
  right: 0;
  width: 196px;
  min-width: 196px;
  max-width: 196px;
  z-index: 3;
  background: #ffffff;
  box-shadow: -10px 0 18px rgba(15, 23, 42, 0.08);
  white-space: nowrap;
}

.pet-list-table thead .sticky-action-column {
  z-index: 5;
  background: #f8fafc;
}

.pet-list-actions {
  width: 172px;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
}

.pet-list-actions .table-action-button {
  height: 30px;
  padding: 0 3px;
  border-radius: 9px;
}

.table-action-button {
  height: 32px;
  padding: 0 10px;
  border: 1px solid #2563eb;
  border-radius: 10px;
  background: #2563eb;
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.table-action-button--ghost {
  border-color: #d7deea;
  background: #ffffff;
  color: #334155;
}

.table-action-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pet-list-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
}

.pet-list-footer__meta {
  color: #64748b;
  font-size: 13px;
}

.pet-list-pagination {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.pet-list-page-size {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #475569;
  font-size: 13px;
}

.pet-list-page-size select {
  height: 34px;
  padding: 0 10px;
  border: 1px solid #d7deea;
  border-radius: 10px;
  background: #ffffff;
}

.pet-list-page-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #d7deea;
  border-radius: 10px;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
}

.pet-list-page-button--active {
  border-color: #2563eb;
  background: #2563eb;
  color: #ffffff;
}

.pet-detail-modal {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.42);
}

.pet-detail-modal__panel {
  width: min(920px, 100%);
  max-height: min(760px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
}

.pet-detail-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 20px 14px;
  border-bottom: 1px solid #eef2f7;
}

.modal-close-button {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: 1px solid #d7deea;
  border-radius: 10px;
  background: #ffffff;
  color: #475569;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.pet-detail-state {
  padding: 20px;
}

.pet-detail-body {
  min-height: 0;
  flex: 1;
  overflow: auto;
  padding: 18px 20px 0;
}

.pet-profile-detail {
  display: grid;
  gap: 16px;
}

.pet-cover-preview {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e8edf3;
  color: #94a3b8;
}

.pet-cover-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pet-profile-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  background: #ffffff;
}

.pet-avatar-preview {
  width: 74px;
  height: 74px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 18px;
  background: #eef4ff;
  color: #2563eb;
  font-size: 22px;
  font-weight: 800;
}

.pet-avatar-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pet-profile-card__copy {
  min-width: 0;
}

.pet-profile-card__name {
  color: #111827;
  font-size: 18px;
  font-weight: 800;
}

.pet-profile-card__meta {
  margin-top: 4px;
  color: #64748b;
  font-size: 13px;
}

.pet-profile-card__intro {
  margin-top: 8px;
  color: #475569;
  font-size: 13px;
  line-height: 20px;
}

.pet-detail-grid,
.pet-cost-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.pet-detail-field {
  min-width: 0;
  padding: 12px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: #f8fafc;
}

.pet-detail-field span {
  display: block;
  color: #64748b;
  font-size: 12px;
  line-height: 16px;
}

.pet-detail-field strong {
  display: block;
  margin-top: 6px;
  color: #111827;
  font-size: 14px;
  line-height: 20px;
  word-break: break-all;
}

.pet-detail-section {
  min-height: 0;
}

.pet-detail-table {
  min-width: 780px;
}

.pet-plan-block {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: #ffffff;
}

.pet-plan-block h4 {
  margin: 0 0 8px;
  color: #111827;
  font-size: 15px;
}

.pet-plan-block p {
  margin: 0;
  color: #475569;
  font-size: 13px;
  line-height: 20px;
  white-space: pre-wrap;
}

.pet-detail-modal__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px 20px;
  color: #64748b;
  font-size: 13px;
}

@media (max-width: 1280px) {
  .pet-list-page {
    grid-template-columns: 1fr;
  }

  .pet-list-sidebar,
  .pet-list-main {
    height: auto;
    min-height: 0;
  }

  .pet-list-sidebar__scroll {
    overflow: visible;
  }

  .pet-list-table-wrap {
    max-height: 520px;
  }
}

@media (max-width: 720px) {
  .pet-list-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .pet-list-pagination {
    justify-content: flex-start;
  }

  .pet-detail-modal {
    padding: 12px;
  }

  .pet-detail-grid,
  .pet-cost-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pet-cover-preview {
    height: 140px;
  }
}
</style>
